import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BlogMobilePreview } from "@/components/studio/blog-mobile-preview";
import { BuilderSection } from "@/components/studio/builder-section";
import { BuilderShell } from "@/components/studio/builder-shell";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { RefinementSection } from "@/components/studio/refinement-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { ContentBlogService } from "@/lib/api/blog.service";
import { categoryService } from "@/lib/api/categories.service";
import { coachService } from "@/lib/api/coachService";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  BlogContent,
  BlogLanguageVariant,
  Category,
  Coach,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import {
  CONTENT_TYPE_CONFIG,
  SUPPORTED_LANGUAGES,
} from "@/lib/studio-constants";
import { validateImageUrl } from "@/lib/image-url-validation";
import { validateThumbnailFile } from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Eye, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { BlogVariantFields } from "./blog-variant-fields";

const blogConfig = CONTENT_TYPE_CONFIG.blog;
const ELEVATED_ROLES = [
  "admin",
  "super_admin",
  "coach_manager",
  "content_manager",
];

function validateBodyImageUrls(body: string | null): string | null {
  if (!body) return null;
  let blocks: unknown;
  try {
    blocks = JSON.parse(body);
  } catch {
    return null;
  }
  if (!Array.isArray(blocks)) return null;
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i] as { type?: string; content?: unknown };
    if (block?.type !== "image") continue;
    const content = typeof block.content === "string" ? block.content : "";
    const result = validateImageUrl(content);
    if (!result.valid) {
      return `Image block ${i + 1}: ${result.error}`;
    }
  }
  return null;
}

interface BlogEditData extends BlogContent {
  variants: BlogLanguageVariant[];
}

interface VariantEdit {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  localizedBody: string | null;
  thumbnailUrl: string | null;
}

export default function BlogEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { uploadFile, isUploading } = useUpload();
  const { user } = useAuth();
  const isCoachOnly =
    !!user?.roles?.includes("coach") &&
    !user.roles.some((r) => ELEVATED_ROLES.includes(r));
  // Server allows restore for admins (any content) and coaches on their OWN
  // content (ownership enforced via assertOwner).
  const isAdmin =
    !!user?.roles?.includes("admin") ||
    !!user?.roles?.includes("super_admin");
  const isCoach = !!user?.roles?.includes("coach");
  const selfCoachId = user?.id ?? null;

  const [categoryId, setCategoryId] = useState("");
  const [selectedTagOptionIds, setSelectedTagOptionIds] = useState<string[]>(
    [],
  );
  const [authorCoachId, setAuthorCoachId] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState("");
  const [variants, setVariants] = useState<VariantEdit[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: blogData, isLoading: isLoadingBlog } = useQuery<BlogEditData>({
    queryKey: ["/content-blogs", id],
    queryFn: async () => {
      const blog = await ContentBlogService.findOne(id);
      const versions = (blog as any).versions || [];
      const latestVersion = versions[0] || {};
      const publishedAt =
        (blog as any).publishedVersion?.publishedAt ??
        latestVersion.publishedAt ??
        null;
      return {
        ...blog,
        variants: latestVersion.languages || [],
        version: latestVersion.version,
        status: latestVersion.status,
        internalNotes: latestVersion.internalNotes,
        publishedAt,
      } as BlogEditData;
    },
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: coaches = [] } = useQuery<Coach[]>({
    queryKey: ["/api/coaches"],
    queryFn: () => coachService.getCoaches().then((r) => r.coaches),
    enabled: !isCoachOnly,
  });

  useEffect(() => {
    if (isCoachOnly && selfCoachId && !authorCoachId && loaded) {
      setAuthorCoachId(selfCoachId);
    }
  }, [isCoachOnly, selfCoachId, authorCoachId, loaded]);

  useEffect(() => {
    if (blogData && !loaded) {
      setCategoryId(blogData.categoryId ?? "");
      setSelectedTagOptionIds(blogData.tagOptionIds);
      setAuthorCoachId(blogData.authorCoachId ?? null);
      setInternalNotes(blogData.internalNotes || "");
      setVariants(
        blogData.variants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || "",
          localizedBody: v.localizedBody ?? null,
          thumbnailUrl: v.thumbnailUrl ?? null,
        })),
      );
      setLoaded(true);
    }
  }, [blogData, loaded]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!categoryId) {
        throw new Error("Please select a category to save this blog.");
      }
      for (const v of variants) {
        const bodyError = validateBodyImageUrls(v.localizedBody);
        if (bodyError) {
          throw new Error(
            `${v.languageCode.toUpperCase()} variant: ${bodyError}`,
          );
        }
      }
      const payload = {
        blog: {
          categoryId,
          tagOptionIds: selectedTagOptionIds,
          authorCoachId,
          internalNotes: internalNotes || null,
        },
        variants: variants.map((v) => ({
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle,
          localizedDescription: v.localizedDescription || null,
          localizedBody: v.localizedBody,
          thumbnailUrl: v.thumbnailUrl,
          translationStatus: v.isDefault ? "completed" : "pending",
        })),
      };
      await ContentBlogService.update(id, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      queryClient.invalidateQueries({ queryKey: ["/content-blogs", id] });
      toast({ title: "Blog content saved" });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to save",
        variant: "destructive",
      });
    },
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!categoryId) {
        throw new Error(
          "Please select a category before submitting for approval.",
        );
      }
      const defaultVar = variants.find((v) => v.isDefault);
      if (!defaultVar?.localizedTitle?.trim()) {
        throw new Error(
          "Please add a title to the default language variant before submitting.",
        );
      }
      if (!defaultVar?.thumbnailUrl) {
        throw new Error(
          "Please upload a hero image (thumbnail) before submitting.",
        );
      }
      await saveMutation.mutateAsync();
      await ContentBlogService.submitForReview(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      queryClient.invalidateQueries({ queryKey: ["/content-blogs", id] });
      toast({ title: "Submitted for approval" });
      setLocation("/studio/blogs");
    },
    onError: (error: Error) => {
      let message = error.message || "Failed to submit";
      try {
        const colonIdx = error.message.indexOf(":");
        if (colonIdx > -1) {
          const body = error.message.substring(colonIdx + 2);
          const parsed = JSON.parse(body);
          if (typeof parsed.error === "string") {
            message = parsed.error;
          } else if (Array.isArray(parsed.error) && parsed.error.length > 0) {
            const issue = parsed.error[0];
            const field = Array.isArray(issue.path)
              ? issue.path.join(".")
              : "field";
            message = `${field}: ${issue.message || "invalid value"}`;
          }
        }
      } catch {}
      toast({
        title: "Cannot submit",
        description: message,
        variant: "destructive",
      });
    },
  });

  const createVersionMutation = useMutation({
    mutationFn: async () => {
      return ContentBlogService.createVersion(id);
    },
    onSuccess: async (data: { id: string; version: number }) => {
      await queryClient.invalidateQueries({
        queryKey: ["/content-blogs", id],
      });
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      setLoaded(false);
      toast({ title: `New version (v${data.version}) created as draft` });
      setLocation(`/studio/blogs/${data.id}/edit`);
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to create new version",
        variant: "destructive",
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async () => {
      return ContentBlogService.restore(id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["/content-blogs", id],
      });
      queryClient.invalidateQueries({ queryKey: ["/content-blogs"] });
      setLoaded(false);
      toast({ title: "Blog restored to draft" });
    },
    onError: (error: Error) => {
      toast({
        title: error.message || "Failed to restore",
        variant: "destructive",
      });
    },
  });

  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "thumbnailUrl",
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid thumbnail",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const result = await uploadFile(file);
    if (result) {
      const updated = [...variants];
      updated[activeVariantIndex] = {
        ...updated[activeVariantIndex],
        [field]: result.objectPath,
      };
      setVariants(updated);
    }
  };

  const updateVariant = (field: keyof VariantEdit, value: any) => {
    const updated = [...variants];
    updated[activeVariantIndex] = {
      ...updated[activeVariantIndex],
      [field]: value,
    };
    setVariants(updated);
  };

  const addLanguageVariant = (langCode: string) => {
    if (variants.some((v) => v.languageCode === langCode)) return;
    setVariants([
      ...variants,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedDescription: "",
        localizedBody: null,
        thumbnailUrl: null,
      },
    ]);
    setActiveVariantIndex(variants.length);
  };

  const confirmRemoveLanguageVariant = () => {
    if (pendingRemoveIndex === null) return;
    if (variants[pendingRemoveIndex].isDefault) return;
    if (variants.length <= 1) {
      toast({
        title: "At least one language variant must remain",
        variant: "destructive",
      });
      setPendingRemoveIndex(null);
      return;
    }
    const updated = variants.filter((_, i) => i !== pendingRemoveIndex);
    setVariants(updated);
    if (activeVariantIndex >= updated.length) {
      setActiveVariantIndex(Math.max(0, updated.length - 1));
    }
    setPendingRemoveIndex(null);
  };

  const setAsDefault = (index: number) => {
    if (index < 0 || index >= variants.length) return;
    const updated = variants.map((v, i) => ({ ...v, isDefault: i === index }));
    setVariants(updated);
    setActiveVariantIndex(index);
  };

  const toggleTagOption = (tagId: string) => {
    setSelectedTagOptionIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId],
    );
  };

  const getGroupOptions = (groupId: string) =>
    tagOptions.filter((to) => to.groupId === groupId);

  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  const activeVariant = variants[activeVariantIndex];

  const {
    withdrawMutation,
    isReadOnly,
    isOwner,
    canCreateVersion,
  } = useContentLifecycle({
    contentType: "blog",
    apiPath: "/content-blogs",
    id,
    status: blogData?.status,
    createdBy: blogData?.createdBy,
    authorCoachId: blogData?.authorCoachId,
    listRoute: "/studio/blogs",
    editRoute: (newId) => `/studio/blogs/${newId}/edit`,
  });

  const primaryTitle =
    variants.find((v) => v.isDefault)?.localizedTitle || undefined;

  const previewVariant =
    variants[activeVariantIndex] ||
    variants.find((v) => v.isDefault) ||
    variants[0];
  const previewCategory =
    categories.find((c) => c.id === categoryId)?.title || null;
  // The assigned coach may differ from the viewer (e.g. admin created the
  // content for another coach). Coaches can't fetch the roster, so resolve the
  // stored author name from the content payload, falling back to the viewer's
  // own name only when they are the assigned coach.
  const resolvedAuthorName: string | null =
    (blogData as any)?.author?.name ??
    (authorCoachId === user?.id ? (user?.name ?? null) : null);
  const previewAuthor =
    coaches.find((c) => c.id === authorCoachId) ||
    (resolvedAuthorName
      ? {
          name: resolvedAuthorName,
          photoUrl:
            (blogData as any)?.author?.coachProfile?.avatarUrl ??
            (authorCoachId === user?.id ? (user?.avatarUrl ?? null) : null),
        }
      : null);

  const isArchivedStatus = blogData?.status === "archived";

  const headerActionButtons = (
    <>
      <Button
        variant="outline"
        onClick={() => setPreviewOpen(true)}
        data-testid="button-preview"
      >
        <Eye className="h-4 w-4 mr-2" />
        Preview
      </Button>
      {isArchivedStatus && (isAdmin || (isCoach && isOwner)) && (
        <Button
          variant="outline"
          onClick={() => restoreMutation.mutate()}
          disabled={restoreMutation.isPending}
          data-testid="button-restore"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Restore to Draft
        </Button>
      )}
    </>
  );

  return (
    <>
      <BuilderShell
        contentTypeConfig={blogConfig}
        mode="edit"
        title={primaryTitle}
        version={blogData?.version}
        status={blogData?.status}
        isLoading={isLoadingBlog}
        notFound={
          !isLoadingBlog && (!blogData || !blogData.version)
        }
        isReadOnly={isReadOnly}
        isSaving={saveMutation.isPending}
        isSubmitting={submitMutation.isPending}
        isCreatingVersion={createVersionMutation.isPending}
        canCreateVersion={canCreateVersion}
        isWithdrawing={withdrawMutation.isPending}
        onSave={() => saveMutation.mutate()}
        onSubmit={() => submitMutation.mutate()}
        onCreateVersion={() => createVersionMutation.mutate()}
        onWithdraw={() => withdrawMutation.mutate(undefined)}
        headerActions={headerActionButtons}
      >
        {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}

        <IntentTaggingSection
          stepNumber={1}
          categories={categories}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          tagGroups={tagGroups}
          getGroupOptions={getGroupOptions}
          selectedTagOptionIds={selectedTagOptionIds}
          onToggleTagOption={toggleTagOption}
          isReadOnly={isReadOnly}
        />

        <BuilderSection
          title="Content"
          stepNumber={2}
          description="Author for this blog"
          defaultOpen={false}
          disabled={isReadOnly}
        >
          <div className="space-y-2">
            <Label>Author</Label>
            {isCoachOnly ? (
              <Input
                value={resolvedAuthorName ?? ""}
                disabled
                data-testid="select-author-coach"
              />
            ) : (
              <Select
                value={authorCoachId || "none"}
                onValueChange={(val) =>
                  setAuthorCoachId(val === "none" ? null : val)
                }
                disabled={isReadOnly}
              >
                <SelectTrigger data-testid="select-author-coach">
                  <SelectValue placeholder="Select author" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No author</SelectItem>
                  {coaches.map((coach) => (
                    <SelectItem key={coach.id} value={coach.id}>
                      {coach.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </BuilderSection>

        <LanguageVariantsSection
          stepNumber={3}
          variants={variants}
          activeVariantIndex={activeVariantIndex}
          onActiveVariantChange={setActiveVariantIndex}
          availableLanguages={availableLanguages}
          onAddVariant={addLanguageVariant}
          onSetDefault={setAsDefault}
          pendingRemoveIndex={pendingRemoveIndex}
          onRequestRemove={setPendingRemoveIndex}
          onConfirmRemove={confirmRemoveLanguageVariant}
          onCancelRemove={() => setPendingRemoveIndex(null)}
          isReadOnly={isReadOnly}
        >
          {activeVariant && (
            <BlogVariantFields
              variant={activeVariant}
              onFieldChange={updateVariant}
              onFileUpload={handleFileUpload}
              isUploading={isUploading}
              isReadOnly={isReadOnly}
            />
          )}
        </LanguageVariantsSection>

        <RefinementSection
          stepNumber={4}
          internalNotes={internalNotes}
          onInternalNotesChange={setInternalNotes}
          isReadOnly={isReadOnly}
        />
      </BuilderShell>

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[480px] overflow-y-auto bg-neutral-100 dark:bg-neutral-900 p-0"
          data-testid="sheet-blog-preview"
        >
          <SheetHeader className="px-6 pt-6 pb-4">
            <SheetTitle>Mobile preview</SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-8 flex justify-center">
            <BlogMobilePreview
              title={previewVariant?.localizedTitle || ""}
              description={previewVariant?.localizedDescription || ""}
              categoryName={previewCategory}
              heroImageUrl={previewVariant?.thumbnailUrl || null}
              bodyJson={previewVariant?.localizedBody || null}
              authorName={previewAuthor?.name || null}
              authorAvatarUrl={previewAuthor?.photoUrl || null}
              publishedAt={blogData?.publishedAt || null}
            />
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
